import _ from 'lodash'
import type {HookEventData} from '../hooks/hooks'
import {DATA_TYPES} from '../../constants/constants'
import type {SchemaExtensionAPI} from '../schema/schema'
import {shouldMergeDataItems} from '../../utils/dataUtils'
import {createHookDefinition} from '../../utils/hooksUtils'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import type {DMApisWithEvents} from '@wix/document-manager-core'
import {ReportableWarning} from '@wix/document-manager-utils'
import {isBuilderData} from '../../utils/builderUtils'

export const nestedToFlatTransformMap = {
    [DATA_TYPES.data]: (dataItem: any) => {
        if (dataItem?.data && dataItem.id) {
            dataItem.data._id = dataItem.id
        }

        return dataItem.data ?? dataItem
    }
}

export const transformBuilderDataToFlat = (createExtArgs: DMApisWithEvents, {dataItem, namespace}: any) => {
    const {schemaAPI} = createExtArgs.extensionAPI as SchemaExtensionAPI

    if (!schemaAPI.isSchemaCollectionRegistered(dataItem?.type)) {
        return dataItem
    }

    return nestedToFlatTransformMap[namespace]?.(dataItem) ?? dataItem
}

export const flatToNestedTransformMap = {
    [DATA_TYPES.data]: (dataItem: any, type: string) => {
        const structuredItem: any = {data: dataItem, type}
        if (dataItem._id) {
            structuredItem.id = dataItem._id
            delete dataItem._id
        }

        if (dataItem.type === type) {
            delete dataItem.type
        }
        return structuredItem
    }
}

const validateRefTypes = (options: any) => {
    if (!options?.refTypes?.length) {
        throw new Error('Trying to set an array item that has no type based on its schema')
    } else if (options.refTypes.length > 1) {
        throw new Error('Trying to set an array item that has more than one allowed type based on its schema')
    }
}

export const transformBuilderDataToNestedObjects = (
    createExtArgs: DMApisWithEvents,
    {serializedFlatDataItem, itemType, itemId, options}: any
) => {
    const {schemaAPI, dataModel} = createExtArgs.extensionAPI as SchemaExtensionAPI & DataModelExtensionAPI
    const logger = createExtArgs.coreConfig?.logger

    if (
        !schemaAPI.isSchemaCollectionRegistered(options?.refTypes?.[0]) &&
        !schemaAPI.isSchemaCollectionRegistered(options?.elementType)
    ) {
        return serializedFlatDataItem
    }

    let dataItemType
    if (!options?.elementType) {
        validateRefTypes(options)
        dataItemType = options?.refTypes?.[0]
    } else {
        dataItemType = schemaAPI.getDataItemTypeBasedOnCompType(options?.elementType)
    }

    if (!isBuilderData(dataItemType)) {
        logger?.captureError(
            new ReportableWarning({
                errorType: 'BuilderDataItemTypeWithoutDataSuffix',
                message: 'Builder data item type is expected to end with .Data',
                extras: {
                    dataItemType,
                    itemType,
                    itemId,
                    elementType: options?.elementType,
                    refTypes: options?.refTypes
                }
            })
        )
    }

    const currentItem = dataModel.getItem(
        itemId ?? serializedFlatDataItem._id,
        itemType,
        undefined,
        options?.languageCode
    )

    let flatData
    if (!options?.skipOriginalNodeMerge && shouldMergeDataItems(currentItem, serializedFlatDataItem)) {
        flatData = _.assign(currentItem, serializedFlatDataItem)
    } else {
        flatData = serializedFlatDataItem
    }

    flatData = _.omitBy(flatData, _.isNil)

    return flatToNestedTransformMap[itemType]?.(flatData, dataItemType) ?? serializedFlatDataItem
}

export interface AppManifestUpdatedEvent extends HookEventData<'app_manifest_updated'> {
    appId: string
}

export const BUILDER_COMPONENTS_HOOKS = {
    HOT_RELOAD: {
        APP_MANIFEST_UPDATED: createHookDefinition<'app_manifest_updated', AppManifestUpdatedEvent>(
            'app_manifest_updated'
        )
    }
}
